import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarClock, Download, Play } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { StandardReportSections } from '@/components/reports/standard/StandardReportSections';
import { StandardRunFailedAlert } from './StandardRunFailedAlert';
import { ScheduleStandardReportDialog } from '@/components/reports/standard/ScheduleStandardReportDialog';
import { useStandardReportRun } from '@/hooks/useStandardReportRun';
import { standardReportsService, saveBlob } from '@/services/standardReports.service';
import { getErrorMessage } from '@/services/apiClient';
import { DATE_RANGE_PRESETS, resolvePreset } from '@/lib/dateRangePresets';
import { invalidateMyReports } from '@/hooks/useMyReports';
import { isTerminalRunStatus } from '@/types/standardReports.types';
import type {
  StandardReportDescriptor,
  StandardReportParams,
} from '@/types/standardReports.types';
import { DatePicker } from '@/components/ui/DatePicker';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { listFacilities } from '@/services/admin.service';
import { listTemplates } from '@/services/memberships.service';
import { useLeadSourcePickerOptions } from '@/hooks/filters/leadSourceOptions';

/** The custom-range escape hatch, kept out of the preset list itself. */
const CUSTOM = '__custom__';

/** The period a report opens on when it declares none of its own. */
const SHARED_DEFAULT_PRESET = 'lastmonth';

/**
 * Runs one standard report and shows the result.
 *
 * THE PERIOD IS SENT AS A KEY, NOT AS DATES — when a preset is chosen. That is
 * the whole point of the key vocabulary: the same choice can be saved as a
 * recurring order, where the server re-resolves it on each fire so every
 * delivery covers the period that just closed. Sending resolved bounds instead
 * would freeze a recurring report on one window forever.
 *
 * A CUSTOM RANGE sends explicit bounds instead, because there is nothing to
 * re-resolve. The server takes either, and rejects both together.
 *
 * WHY THE PRESET LIST IS IMPORTED RATHER THAN DECLARED. This page once carried
 * its own bare <select> over its own vocabulary, and the server had a third one
 * that resolved nothing — so "Last Month" ran over all time and reported itself
 * successful. DATE_RANGE_PRESETS is the same list every other filter surface
 * uses, and the server resolves those same keys. One vocabulary, or this
 * happens again.
 */
export default function StandardReportRunnerPage() {
  const { reportKey = '' } = useParams();
  const navigate = useNavigate();

  const [descriptor, setDescriptor] = useState<StandardReportDescriptor | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  // The period the user picked on THIS report, or null to follow the report's
  // own default.
  const [chosenPreset, setChosenPreset] = useState<string | null>(null);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduled, setScheduled] = useState(false);
  const [facilityIds, setFacilityIds] = useState<string[]>([]);
  const [templateIds, setTemplateIds] = useState<string[]>([]);
  const [leadSourceIds, setLeadSourceIds] = useState<string[]>([]);

  const { run, status, running, error, timedOut, start } = useStandardReportRun(reportKey);

  // This page stays MOUNTED when the route moves to another report, so its
  // state carries across. The period choice and the tier choice belong to the
  // report they were made on: a tier choice left behind would be sent to — or
  // silently reappear on — a report that never offered it.
  useEffect(() => {
    setChosenPreset(null);
    setTemplateIds([]);
    setLeadSourceIds([]);
  }, [reportKey]);

  const defaultPreset =
    descriptor?.key === reportKey && descriptor.defaultDatePreset
      ? descriptor.defaultDatePreset
      : SHARED_DEFAULT_PRESET;

  // Derived rather than synced: an untouched period follows the report's
  // default when its entry arrives, and a choice made while the next report is
  // still loading is kept.
  const preset = chosenPreset ?? defaultPreset;
  const qc = useQueryClient();

  // A finished run IS a row on My Reports — a report run straight from the
  // catalog has no order behind it, so the run is the only thing representing
  // it there. That list caches its sources for 30s and this page invalidated
  // nothing, so a user who ran a report and went to look for it found the list
  // exactly as they left it and had to reload the browser.
  //
  // Keyed on `status`, not `run`: the hook only replaces `run` once, and it is
  // the polled status that tells us the run has actually finished.
  useEffect(() => {
    if (status && isTerminalRunStatus(status)) invalidateMyReports(qc);
  }, [status, qc]);

  const isCustom = preset === CUSTOM;

  useEffect(() => {
    let cancelled = false;

    standardReportsService
      .catalog()
      .then((reports) => {
        if (cancelled) return;

        const match = reports.find((r) => r.key === reportKey) ?? null;

        // A report with its own page (an old link, or a typed URL) cannot be
        // run from here: the server refuses it. Send the user to its page.
        if (match?.route) {
          navigate(match.route, { replace: true });
          return;
        }

        setDescriptor(match);

        // Not in the catalog means not allow-listed or above this user's level.
        // The run endpoint would refuse it too, so say so rather than offering
        // a button that cannot work.
        if (!match) setCatalogError('This report is not available to you.');
      })
      .catch((e) => {
        if (!cancelled) setCatalogError(getErrorMessage(e, 'Could not load this report.'));
      });

    return () => {
      cancelled = true;
    };
  }, [reportKey, navigate]);

  const periodOptions = useMemo(
    () => [
      ...DATE_RANGE_PRESETS.map((p) => ({ value: p.key as string, label: p.label })),
      { value: CUSTOM, label: 'Custom range…' },
    ],
    [],
  );

  // Shown beside the picker so the dates are visible BEFORE running. The server
  // resolves the same key independently; a test in each repo pins them to agree.
  const resolved = useMemo(() => {
    if (isCustom) return { from: customFrom, to: customTo };

    try {
      return resolvePreset(preset as never);
    } catch {
      return { from: '', to: '' };
    }
  }, [preset, isCustom, customFrom, customTo]);

  const canRun = !running && (!isCustom || (customFrom !== '' && customTo !== ''));

  // Rendered only when the report declares it. Driving off `filterKeys` rather
  // than off the report key means every report that accepts a facility gets the
  // control, and one that does not cannot be sent a filter the server would
  // silently ignore.
  const acceptsFacility = descriptor?.filterKeys?.includes('facilityIds') ?? false;

  const { data: facilities } = useQuery({
    // Same key the payments filters use, so the two share one cached fetch.
    queryKey: ['facilities'],
    queryFn: () => listFacilities(),
    staleTime: 60_000,
    enabled: acceptsFacility,
  });

  const facilityOptions = useMemo(
    () =>
      (facilities ?? [])
        .filter((f) => f.active)
        .map((f) => ({ value: String(f.facilityId), label: f.facilityName })),
    [facilities],
  );

  // Same rule as the facility control: shown only to a report that reads it.
  const acceptsTier = descriptor?.filterKeys?.includes('templateIds') ?? false;

  const { data: tiers } = useQuery({
    // Its own key: the payments filters cache ACTIVE templates only, and a
    // report over past months still has members on tiers retired since.
    queryKey: ['membership-templates-report-filter'],
    queryFn: () => listTemplates({ limit: 500, allStatuses: true }),
    staleTime: 5 * 60_000,
    enabled: acceptsTier,
  });

  const tierOptions = useMemo(
    () =>
      (tiers ?? []).map((t) => ({
        value: String(t.templateId),
        label: t.active ? t.title : `${t.title} (inactive)`,
      })),
    [tiers],
  );

  // Shown only to a report that reads it, like the facility and tier controls.
  // The values are the Leads list's own: a source id, or `filtersgroup-<id>`
  // for a whole category, so they go to the server as strings.
  const acceptsLeadSource = descriptor?.filterKeys?.includes('leadSourceIds') ?? false;

  const leadSourceOptions = useLeadSourcePickerOptions({ enabled: acceptsLeadSource });

  // The tier and lead source choices, sent with a run and kept on a scheduled
  // order. A key is omitted when nothing is chosen: an empty array would read
  // as "none" to a filter that treats the key as a whitelist.
  const choiceFilters = useMemo(() => {
    const filters: Pick<StandardReportParams, 'templateIds' | 'leadSourceIds'> = {};
    if (acceptsTier && templateIds.length > 0) filters.templateIds = templateIds.map(Number);
    if (acceptsLeadSource && leadSourceIds.length > 0) filters.leadSourceIds = leadSourceIds;
    return filters;
  }, [acceptsTier, templateIds, acceptsLeadSource, leadSourceIds]);

  const handleRun = () => {
    // Either a relative key or explicit bounds — never both. The server rejects
    // the pair, and a recurring order needs the key to stay relative.
    const params: StandardReportParams = isCustom
      ? { dateFrom: customFrom, dateTo: customTo }
      : { datePreset: preset };

    // Omitted entirely when nothing is chosen: an empty array would read as
    // "no facilities" to a filter that treats the key as a whitelist.
    if (acceptsFacility && facilityIds.length > 0) {
      params.facilityIds = facilityIds.map(Number);
    }

    void start({ ...params, ...choiceFilters });
  };

  const handleDownload = async () => {
    if (!run) return;

    setDownloading(true);
    setDownloadError(null);

    try {
      const blob = await standardReportsService.downloadRun(run.runId);
      saveBlob(blob, `${run.reportKey}_${run.runId}.xlsx`);
    } catch (e) {
      setDownloadError(getErrorMessage(e, 'Could not download the workbook.'));
    } finally {
      setDownloading(false);
    }
  };

  const downloadable = run?.status === 'ready' && run.hasFile;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 p-6">
        <button
          type="button"
          onClick={() => navigate('/reports')}
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4" />
          New Report
        </button>

        <PageHeader
          variant="compact"
          title={descriptor?.title ?? 'Standard Report'}
          actions={
            descriptor ? (
              <div className="flex gap-2">
                {downloadable && (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Download className="h-4 w-4" />}
                    onClick={handleDownload}
                    disabled={downloading}
                    loading={downloading}
                  >
                    Download workbook
                  </Button>
                )}
                {/* Scheduling is the point of the feature, so it sits beside Run
                    rather than hidden behind a completed result. */}
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<CalendarClock className="h-4 w-4" />}
                  onClick={() => setScheduling(true)}
                >
                  Schedule
                </Button>
                <Button
                  size="sm"
                  leftIcon={<Play className="h-4 w-4" />}
                  onClick={handleRun}
                  disabled={!canRun}
                  loading={running}
                >
                  Run report
                </Button>
              </div>
            ) : null
          }
        />

        {catalogError && <Alert variant="error">{catalogError}</Alert>}

        {descriptor && (
          <>
            <p className="mb-4 max-w-3xl text-sm text-slate-500">{descriptor.summary}</p>

            <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4">
              <div className="w-56">
                <Select
                  label="Period"
                  options={periodOptions}
                  value={preset}
                  onChange={(v) => {
                    setChosenPreset((v as string) ?? null);
                  }}
                />
              </div>

              {isCustom ? (
                <>
                  <div className="w-44">
                    <DatePicker
                      id="standard-report-from"
                      label="From"
                      value={customFrom}
                      onChange={setCustomFrom}
                    />
                  </div>
                  <div className="w-44">
                    <DatePicker
                      id="standard-report-to"
                      label="To"
                      value={customTo}
                      onChange={setCustomTo}
                    />
                  </div>
                </>
              ) : (
                resolved.from && (
                  <span className="pb-2 text-xs text-slate-500">
                    {resolved.from} → {resolved.to}
                  </span>
                )
              )}

              {acceptsFacility && (
                <div className="w-64">
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Locations
                  </label>
                  <SearchableMultiSelect
                    options={facilityOptions}
                    selectedValues={facilityIds}
                    onSelectionChange={setFacilityIds}
                    placeholder="All locations"
                    searchPlaceholder="Search locations…"
                    emptyMessage="No locations found"
                  />
                </div>
              )}

              {acceptsTier && (
                <div className="w-64">
                  <label
                    htmlFor="standard-report-tiers"
                    className="mb-1 block text-sm font-medium text-slate-700"
                  >
                    Tiers
                  </label>
                  <SearchableMultiSelect
                    id="standard-report-tiers"
                    options={tierOptions}
                    selectedValues={templateIds}
                    onSelectionChange={setTemplateIds}
                    placeholder="All tiers"
                    searchPlaceholder="Search tiers…"
                    emptyMessage="No tiers found"
                  />
                </div>
              )}

              {acceptsLeadSource && (
                <div className="w-64">
                  <label
                    htmlFor="standard-report-lead-sources"
                    className="mb-1 block text-sm font-medium text-slate-700"
                  >
                    Lead Sources
                  </label>
                  <SearchableMultiSelect
                    id="standard-report-lead-sources"
                    options={leadSourceOptions}
                    selectedValues={leadSourceIds}
                    onSelectionChange={setLeadSourceIds}
                    placeholder="All sources"
                    searchPlaceholder="Search lead sources…"
                    emptyMessage="No lead sources found"
                  />
                </div>
              )}
            </div>

            {scheduled && (
              <Alert variant="success">
                Scheduled. It will appear in My Reports, with each delivery listed under it.
              </Alert>
            )}

            {running && (
              <Alert variant="info">
                {status === 'delivering' ? 'Sending the report…' : 'Building the report…'} This runs
                in the background, so you can leave this page and find the result in My Reports.
              </Alert>
            )}

            {timedOut && (
              <Alert variant="warning">
                This is taking longer than expected. The report is still running — check My Reports
                in a few minutes rather than starting it again.
              </Alert>
            )}

            {error && <Alert variant="error">{error}</Alert>}
            {downloadError && <Alert variant="error">{downloadError}</Alert>}

            {/* `empty` and `too_large` are answers, not errors — saying "failed"
                would tell the user something broke when nothing did. */}
            {run?.status === 'empty' && (
              <Alert variant="info">Nothing matched this period. Try a wider one.</Alert>
            )}

            {/* The server now explains exactly what to change — it names the row
                count and the cap. Preferring its message to a fixed sentence is
                the difference between "too large" and "397,793 rows, narrow it". */}
            {run?.status === 'too_large' && (
              <Alert variant="warning">
                {run.refusalReason ??
                  'This period covers more than one report can hold. Narrow it and run again.'}
              </Alert>
            )}

            {run?.status === 'failed' && <StandardRunFailedAlert />}
          </>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-6 pb-6">
        {run?.preview && (run.status === 'ready' || run.status === 'delivering') && (
          <StandardReportSections
            preview={run.preview}
            truncated={run.truncated}
            totalRowCount={run.rowCount}
          />
        )}
      </div>

      {scheduling && descriptor && (
        <ScheduleStandardReportDialog
          reportKey={reportKey}
          reportTitle={descriptor.title}
          initialPreset={isCustom ? defaultPreset : preset}
          // The tier and lead source choices travel with the order; a
          // scheduled report that quietly widened to every tier or source
          // would read as the filtered one.
          filters={Object.keys(choiceFilters).length > 0 ? choiceFilters : undefined}
          onClose={() => setScheduling(false)}
          onSaved={() => {
            setScheduling(false);
            setScheduled(true);
            // Scheduling creates an ORDER, which is a My Reports row in its own
            // right and appears there before it has ever fired.
            invalidateMyReports(qc);
          }}
        />
      )}
    </div>
  );
}
